import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/lib/auth/AuthContext';
import { needsOrganization } from '../api/social-errors';
import { useSocialProviders, useSocialSignIn } from '../api/social-auth';

const GOOGLE_SDK = 'https://accounts.google.com/gsi/client';
const APPLE_SDK = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

/**
 * Google renders its button inside an iframe we cannot restyle, so matching the
 * two means matching *its* palette rather than ours. These are the colours
 * Google's own `outline` and `filled_black` themes use; the Apple button copies
 * them so the pair reads as one control group instead of two bolted together.
 */
const GOOGLE_LIGHT = { bg: '#FFFFFF', text: '#1F1F1F', border: '#747775' };
const GOOGLE_DARK = { bg: '#131314', text: '#E3E3E3', border: '#8E918F' };

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: Record<string, unknown>) => void;
        signIn: () => Promise<{ authorization?: { id_token?: string } }>;
      };
    };
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          prompt: () => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

interface Props {
  /** Shown instead of running the flow, when a prerequisite is missing. */
  disabledReason?: string;
  onError: (message: string) => void;
  /**
   * The sign-in succeeded as far as the provider is concerned, but there is no
   * account here and no practice named to create one for. Send them to
   * "Register your practice" rather than showing an error beside a sign-in
   * form they have already completed. Without this handler the refusal falls
   * through to `onError`, which is right on the signup form itself.
   */
  onNeedsOrganization?: () => void;
  onSuccess: () => void;
  /** Names the organization created if this sign-in registers a new account. */
  organizationName?: string;
}

export function SocialAuthButtons({
  onError,
  onSuccess,
  disabledReason,
  organizationName,
  onNeedsOrganization,
}: Props) {
  const { data: providers } = useSocialProviders();
  const signIn = useSocialSignIn();
  const { loginWithTokens } = useAuth();
  const { resolvedTheme } = useTheme();
  const [busy, setBusy] = useState<string | null>(null);
  const googleSlot = useRef<HTMLDivElement | null>(null);
  const [slotWidth, setSlotWidth] = useState(0);
  // Read inside Google's callback, which closes over its first render.
  const disabledReasonRef = useRef(disabledReason);
  disabledReasonRef.current = disabledReason;

  const google = providers?.find((p) => p.provider === 'google');
  const apple = providers?.find((p) => p.provider === 'apple');
  const isDark = resolvedTheme === 'dark';
  const palette = isDark ? GOOGLE_DARK : GOOGLE_LIGHT;

  /**
   * Google's button takes a pixel width and will not accept a percentage, so it
   * has to be measured. Without this it renders at a fixed width while the
   * Apple button is fluid, which is what made the two look unrelated.
   */
  useEffect(() => {
    const el = googleSlot.current;
    if (!el) return;
    const measure = () => setSlotWidth(Math.round(el.getBoundingClientRect().width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [google?.client_id]);

  const complete = async (provider: string, idToken: string) => {
    try {
      const tokens = await signIn.mutateAsync({ idToken, provider, organizationName });
      await loginWithTokens(tokens);
      onSuccess();
    } catch (err: unknown) {
      // One refusal is not an error to read but a step to take: no account
      // here, and no practice named to create one for. Hand it to the caller,
      // which moves them to the form that asks for the clinic name.
      if (onNeedsOrganization && needsOrganization(err)) {
        onNeedsOrganization();
        return;
      }
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      // The API's refusals are written to be read by the person who hit them,
      // and none of them leak whether an account exists.
      onError(detail ?? 'That sign-in did not work. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!google || !googleSlot.current || !slotWidth) return;
    let cancelled = false;

    loadScript(GOOGLE_SDK)
      .then(() => {
        if (cancelled || !googleSlot.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: google.client_id,
          callback: (response: { credential?: string }) => {
            // Google's button lives in an iframe and cannot be disabled from
            // here, so the prerequisite is enforced on the way back instead.
            if (disabledReasonRef.current) {
              onError(disabledReasonRef.current);
              return;
            }
            if (response.credential) {
              setBusy('google');
              void complete('google', response.credential);
            }
          },
        });
        googleSlot.current.replaceChildren();
        window.google.accounts.id.renderButton(googleSlot.current, {
          shape: 'pill',
          size: 'large',
          width: slotWidth,
          type: 'standard',
          text: 'continue_with',
          logo_alignment: 'left',
          // `outline` is a white button, invisible on a dark page; filled_black
          // is Google's supported dark equivalent.
          theme: isDark ? 'filled_black' : 'outline',
        });
      })
      .catch(() => onError('Could not reach Google. Try again, or sign in with your password.'));

    return () => {
      cancelled = true;
    };
     
  }, [google?.client_id, slotWidth, isDark]);

  const signInWithApple = async () => {
    if (!apple) return;

    if (disabledReason) {
      onError(disabledReason);
      return;
    }

    if (!appleCanWork()) {
      onError('Apple sign-in needs a secure (https) address, so it cannot run on this local server. Use Google or your password here.');
      return;
    }

    setBusy('apple');
    try {
      await loadScript(APPLE_SDK);
      if (!window.AppleID) throw new Error('sdk missing');

      window.AppleID.auth.init({
        // A popup keeps the ID token in the browser. The redirect flow would
        // POST a form back to redirectURI instead, which this SPA cannot receive.
        usePopup: true,
        scope: 'name email',
        clientId: apple.client_id,
        // From the API, because it has to match a Return URL registered on the
        // Services ID — which the browser cannot know, and which is not
        // necessarily a route this app serves. Apple never navigates here in
        // the popup flow; it only checks the value is registered.
        redirectURI: apple.redirect_uri || `${window.location.origin}/login`,
      });

      const result = await window.AppleID.auth.signIn();
      const idToken = result?.authorization?.id_token;
      if (!idToken) throw new Error('no id_token');
      await complete('apple', idToken);
    } catch {
      setBusy(null);
      onError('Apple sign-in was cancelled or did not complete.');
    }
  };

  if (!providers || providers.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {google && <div ref={googleSlot} className="h-10 w-full [color-scheme:light]" />}

      {apple && (
        <button
          type="button"
          disabled={busy !== null}
          style={{ color: palette.text, backgroundColor: palette.bg, borderColor: palette.border }}
          className="relative flex h-10 w-full items-center justify-center rounded-full border text-[14px] font-medium transition-opacity duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={signInWithApple}>
          {/* Google\'s logo_alignment:\'left\' pins the mark to the leading edge and
              centres the label independently. Absolute positioning reproduces that,
              so the two rows line up rather than one being centred as a logo+label
              pair and the other not. */}

          <span className="absolute left-3 flex items-center">
            <AppleMark />
          </span>
          {busy === 'apple' ? 'Signing in…' : 'Continue with Apple'}
        </button>
      )}
    </div>
  );
}

/**
 * Apple refuses any return URL that is not HTTPS, and refuses localhost
 * outright — there is no developer exemption the way there is for WebAuthn.
 * So Sign in with Apple cannot work on a dev origin at all, and opening the
 * popup only to have Apple answer "invalid_request / Invalid web redirect url"
 * wastes the click and reads as a bug in this app. Checked before init.
 */
function appleCanWork(): boolean {
  return window.location.protocol === 'https:';
}

/** Apple's mark, following the button's own text colour so it works either theme. */
function AppleMark() {
  return (
    <svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24" className="size-[17px]">
      <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 2.99-.85.96-2.24 1.7-3.37 1.61a3.6 3.6 0 0 1 1.15-2.86c.77-.86 2.12-1.5 3.34-1.74zM20.5 17.13c-.6 1.38-.88 1.99-1.65 3.2-1.08 1.7-2.6 3.81-4.48 3.82-1.67.02-2.1-1.09-4.37-1.08-2.27.01-2.74 1.1-4.41 1.09-1.88-.02-3.32-1.93-4.4-3.62C-1.9 16.05-2.2 10.5.34 7.6c1.13-1.32 2.9-2.15 4.6-2.15 1.72 0 2.8 1.09 4.22 1.09 1.38 0 2.22-1.09 4.21-1.09 1.5 0 3.1.82 4.24 2.24-3.72 2.04-3.12 7.35.89 9.44z" />
    </svg>
  );
}

/**
 * Load a provider SDK once per page.
 *
 * Both SDKs register globals and misbehave if evaluated twice, so this keys off
 * the src already being in the document rather than tracking state in React — a
 * remount must not re-inject the tag.
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve();
      else existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('sdk failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error('sdk failed to load'));
    document.head.appendChild(script);
  });
}
